
import React, { useEffect } from 'react';
import { AppNotification } from '../../types';
import { X, CheckCircle, AlertTriangle, Info, AlertCircle } from 'lucide-react';

interface ToastProps {
  notification: AppNotification;
  onClose: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({ notification, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(notification.id);
    }, 5000); // Auto close after 5s

    return () => clearTimeout(timer);
  }, [notification, onClose]);

  const getIcon = () => {
    switch (notification.type) {
      case 'SUCCESS': return <CheckCircle size={18} className="text-green-400" />;
      case 'WARNING': return <AlertTriangle size={18} className="text-orange-400" />;
      case 'ERROR': return <AlertCircle size={18} className="text-red-400" />;
      default: return <Info size={18} className="text-blue-400" />;
    }
  };

  const getBorderColor = () => {
    switch (notification.type) {
      case 'SUCCESS': return 'border-green-500/30 bg-green-900/10';
      case 'WARNING': return 'border-orange-500/30 bg-orange-900/10';
      case 'ERROR': return 'border-red-500/30 bg-red-900/10';
      default: return 'border-blue-500/30 bg-blue-900/10';
    }
  };

  return (
    <div className={`
      flex items-start gap-3 p-4 rounded-md border backdrop-blur-md shadow-2xl min-w-[300px] max-w-sm animate-in slide-in-from-right duration-300
      ${getBorderColor()}
    `}>
      <div className="mt-0.5 shrink-0">{getIcon()}</div>
      <div className="flex-1">
        <h4 className="text-sm font-bold text-white leading-tight">{notification.title}</h4>
        <p className="text-xs text-neutral-300 mt-1 leading-relaxed">{notification.message}</p>
      </div>
      <button onClick={() => onClose(notification.id)} className="text-neutral-500 hover:text-white transition">
        <X size={14} />
      </button>
    </div>
  );
};
